import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectsApi, type Project } from './api';

export interface AsyncState<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
}

/** Runs `load` on mount and whenever `deps` change; `reload` re-runs it. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(load, deps);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await run());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    run()
      .then((value) => {
        if (!cancelled) {
          setData(value);
          setError(null);
        }
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [run]);

  return { data, error, loading, reload };
}

/** Selected project kept in the URL (?project=ID); defaults to the first accessible project. */
export function useProjectSelection(options: { allowUnassigned?: boolean } = {}) {
  const projects = useAsync(projectsApi.list, []);
  const [params, setParams] = useSearchParams();
  const raw = params.get('project');

  const list = projects.data ?? [];
  let selected: Project | null = null;
  let unassigned = false;
  if (options.allowUnassigned && raw === 'unassigned') unassigned = true;
  else selected = list.find((p) => String(p.id) === raw) ?? list[0] ?? null;

  const select = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('project', value);
    setParams(next, { replace: true });
  };

  return { projects, list, selected, unassigned, select };
}
